import { useState } from 'react'
import { ArrowLeft, ArrowRight, Check, Database } from 'lucide-react'
import { supabase, isSupabaseConfigured, updateSupabaseConfig } from '@/lib/supabase'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface OnboardingWizardProps {
  onComplete: () => void
  needsSupabaseSetup?: boolean
  onSupabaseConfigured?: () => void
}

type WizardStep = -1 | 0 | 1 | 2 | 3

interface LensData {
  identity: string
  context: string
  aspiration: string
}

export default function OnboardingWizard({ onComplete, needsSupabaseSetup = false, onSupabaseConfigured }: OnboardingWizardProps) {
  const [step, setStep] = useState<WizardStep>(needsSupabaseSetup ? -1 : 0)
  const [lensData, setLensData] = useState<LensData>({
    identity: '',
    context: '',
    aspiration: '',
  })
  const [supabaseUrl, setSupabaseUrl] = useState('')
  const [supabaseKey, setSupabaseKey] = useState('')
  const [testingConnection, setTestingConnection] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const totalSteps = needsSupabaseSetup ? 5 : 4
  const displayStep = needsSupabaseSetup ? step + 1 : step

  const handleTestConnection = async () => {
    if (!supabaseUrl || !supabaseKey) {
      setError('Preencha a URL e a chave do Supabase')
      return
    }

    setTestingConnection(true)
    setError(null)

    try {
      // Test connection by making a simple query
      const { createClient } = await import('@supabase/supabase-js')
      const testClient = createClient(supabaseUrl, supabaseKey)
      const { error: testError } = await testClient.from('domains').select('id').limit(1)
      
      if (testError) {
        throw new Error(testError.message)
      }

      // Connection successful - save config and reload
      updateSupabaseConfig(supabaseUrl, supabaseKey)
      // The page will reload, but we'll call onSupabaseConfigured just in case
      if (onSupabaseConfigured) {
        onSupabaseConfigured()
      }
    } catch (err) {
      setError(err instanceof Error ? `Erro de conexão: ${err.message}` : 'Erro ao conectar')
    } finally {
      setTestingConnection(false)
    }
  }

  const handleNext = () => {
    if (step < 3) {
      setStep((step + 1) as WizardStep)
    }
  }

  const handleBack = () => {
    const minStep = needsSupabaseSetup ? -1 : 0
    if (step > minStep) {
      setStep((step - 1) as WizardStep)
    }
  }

  const handleFinish = async () => {
    if (!isSupabaseConfigured()) {
      setError('Supabase nao configurado. Configure nas Configuracoes primeiro.')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const lenses = [
        {
          lens_type: 'identity',
          content: lensData.identity,
          is_active: true,
          version: 1,
          updated_at: new Date().toISOString(),
        },
        {
          lens_type: 'context',
          content: lensData.context,
          is_active: true,
          version: 1,
          updated_at: new Date().toISOString(),
        },
        {
          lens_type: 'aspiration',
          content: lensData.aspiration,
          is_active: true,
          version: 1,
          updated_at: new Date().toISOString(),
        },
      ]

      for (const lens of lenses) {
        if (lens.content.trim()) {
          const { error: insertError } = await supabase
            .from('lenses')
            .upsert(lens, { onConflict: 'lens_type' })

          if (insertError) throw insertError
        }
      }

      onComplete()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar lentes')
    } finally {
      setSaving(false)
    }
  }

  const canProceed = () => {
    switch (step) {
      case -1: return supabaseUrl.trim().length > 0 && supabaseKey.trim().length > 0
      case 0: return true
      case 1: return lensData.identity.trim().length > 0
      case 2: return lensData.context.trim().length > 0
      case 3: return lensData.aspiration.trim().length > 0
      default: return false
    }
  }

  return (
    <div className="min-h-screen bg-[#010110] flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-rsb-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-domain-tech/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-domain-business/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} className="flex items-center flex-1">
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300
                    ${i <= displayStep
                      ? 'bg-rsb-accent text-white'
                      : 'bg-rsb-border/30 text-rsb-muted'
                    }
                  `}
                  aria-label={`Passo ${i + 1} de ${totalSteps}`}
                >
                  {i < displayStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    i + 1
                  )}
                </div>
                {i < totalSteps - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${
                    i < displayStep ? 'bg-rsb-accent' : 'bg-rsb-border/30'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <Progress value={((displayStep + 1) / totalSteps) * 100} className="h-1" />
        </div>

        {/* Card */}
        <Card className="card-glass-lg" role="region" aria-label={`Passo ${step + 1} do assistente de configuracao`}>
          <CardContent className="p-8">
            {/* Step -1: Supabase Setup */}
            {step === -1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-6xl mb-3">🔌</div>
                  <h1 className="text-3xl font-display text-gradient-primary mb-3">
                    Conectar ao Supabase
                  </h1>
                  <p className="text-rsb-text-secondary text-lg max-w-md mx-auto">
                    Primeiro, vamos conectar ao seu banco de dados. Você precisa das credenciais
                    do seu projeto Supabase.
                  </p>
                </div>

                <div className="space-y-4 max-w-md mx-auto">
                  <div>
                    <Label htmlFor="supabase-url" className="block text-sm font-medium text-rsb-text-secondary mb-2">
                      URL do Projeto
                    </Label>
                    <Input
                      id="supabase-url"
                      type="url"
                      value={supabaseUrl}
                      onChange={(e) => setSupabaseUrl(e.target.value)}
                      placeholder="https://seu-projeto.supabase.co"
                      className="bg-[#0A0A0F]/50 border-white/10"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="supabase-key" className="block text-sm font-medium text-rsb-text-secondary mb-2">
                      Anon Key (chave pública)
                    </Label>
                    <Input
                      id="supabase-key"
                      type="password"
                      value={supabaseKey}
                      onChange={(e) => setSupabaseKey(e.target.value)}
                      placeholder="eyJhbGciOiJIUzI1NiIs..."
                      className="bg-[#0A0A0F]/50 border-white/10"
                    />
                  </div>

                  <div className="p-3 bg-rsb-accent/10 rounded-lg text-sm text-rsb-text-secondary">
                    <Database className="w-4 h-4 inline mr-2 text-rsb-accent" />
                    Encontre essas credenciais em: Supabase Dashboard → Settings → API
                  </div>
                </div>
              </div>
            )}

            {/* Step 0: Welcome */}
            {step === 0 && (
              <div className="text-center space-y-6">
                <div className="text-6xl animate-float">🧠</div>
                <div>
                  <h1 className="text-3xl font-display text-gradient-primary mb-3">
                    Bem-vindo ao Real Second Brain!
                  </h1>
                  <p className="text-rsb-text-secondary text-lg max-w-md mx-auto">
                    Vamos calibrar seu segundo cerebro. Em 3 passos rapidos, vamos configurar
                    suas lentes cognitivas para personalizar toda a experiencia.
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto pt-4">
                  <div className="text-center">
                    <div className="text-2xl mb-1">🪞</div>
                    <div className="text-xs text-rsb-muted">Identidade</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-1">🎯</div>
                    <div className="text-xs text-rsb-muted">Contexto</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-1">🚀</div>
                    <div className="text-xs text-rsb-muted">Aspiracao</div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Identity Lens */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-4xl mb-3">🪞</div>
                  <h2 className="text-2xl font-display text-rsb-text mb-2">Quem e voce?</h2>
                  <p className="text-rsb-text-secondary">
                    Descreva sua identidade profissional e intelectual. Como voce pensa?
                    Quais sao suas areas de atuacao?
                  </p>
                </div>
                <div>
                  <Label htmlFor="identity-lens" className="block text-sm font-medium text-rsb-text-secondary mb-2">
                    Lente de Identidade
                  </Label>
                  <Textarea
                    id="identity-lens"
                    value={lensData.identity}
                    onChange={(e) => setLensData(prev => ({ ...prev, identity: e.target.value }))}
                    placeholder="Ex: Sou engenheiro de software com 8 anos de experiencia, focado em arquitetura de sistemas distribuidos. Penso em sistemas, gosto de conectar conceitos de diferentes dominios..."
                    className="min-h-[160px] resize-y"
                    aria-describedby="identity-hint"
                  />
                  <p id="identity-hint" className="text-xs text-rsb-muted mt-2">
                    Inclua: nome, areas de expertise, estilo de pensamento, formacao relevante.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Context Lens */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-4xl mb-3">🎯</div>
                  <h2 className="text-2xl font-display text-rsb-text mb-2">O que esta fazendo agora?</h2>
                  <p className="text-rsb-text-secondary">
                    Descreva seu contexto atual. Em que projetos esta trabalhando?
                    Que problemas esta tentando resolver?
                  </p>
                </div>
                <div>
                  <Label htmlFor="context-lens" className="block text-sm font-medium text-rsb-text-secondary mb-2">
                    Lente de Contexto
                  </Label>
                  <Textarea
                    id="context-lens"
                    value={lensData.context}
                    onChange={(e) => setLensData(prev => ({ ...prev, context: e.target.value }))}
                    placeholder="Ex: Estou liderando a migracao de um monolito para microsservicos, estudando Domain-Driven Design, e tentando melhorar a observabilidade do sistema..."
                    className="min-h-[160px] resize-y"
                    aria-describedby="context-hint"
                  />
                  <p id="context-hint" className="text-xs text-rsb-muted mt-2">
                    Inclua: projetos atuais, problemas em aberto, perguntas recorrentes.
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Aspiration Lens */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-4xl mb-3">🚀</div>
                  <h2 className="text-2xl font-display text-rsb-text mb-2">Onde quer chegar?</h2>
                  <p className="text-rsb-text-secondary">
                    Descreva suas aspiracoes. Que habilidades quer desenvolver?
                    Qual e sua visao de futuro?
                  </p>
                </div>
                <div>
                  <Label htmlFor="aspiration-lens" className="block text-sm font-medium text-rsb-text-secondary mb-2">
                    Lente de Aspiracao
                  </Label>
                  <Textarea
                    id="aspiration-lens"
                    value={lensData.aspiration}
                    onChange={(e) => setLensData(prev => ({ ...prev, aspiration: e.target.value }))}
                    placeholder="Ex: Quero me tornar referencia em arquitetura de sistemas, dominar machine learning aplicado a negocios, e construir uma startup de educacao. Anti-goals: nao quero ficar preso em gestao burocratica..."
                    className="min-h-[160px] resize-y"
                    aria-describedby="aspiration-hint"
                  />
                  <p id="aspiration-hint" className="text-xs text-rsb-muted mt-2">
                    Inclua: visao de futuro, skills desejadas, anti-goals (o que evitar).
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-sm" role="alert">
                {error}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-rsb-border/30">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={step === -1 || step === 0}
                className={`${(step === -1 || step === 0) ? 'invisible' : ''}`}
                aria-label="Voltar ao passo anterior"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>

              {step === -1 ? (
                <Button
                  onClick={handleTestConnection}
                  disabled={!canProceed() || testingConnection}
                  aria-label="Testar conexao com Supabase"
                >
                  {testingConnection ? (
                    <>
                      <span className="inline-block w-4 h-4 loading-spinner border-2 mr-2 align-middle" />
                      Testando...
                    </>
                  ) : (
                    <>
                      Conectar
                      <Database className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              ) : step < 3 ? (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  aria-label="Avancar para o proximo passo"
                >
                  {step === 0 ? 'Vamos comecar!' : 'Proximo'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleFinish}
                  disabled={!canProceed() || saving}
                  aria-label="Salvar lentes e comecar a usar"
                >
                  {saving ? (
                    <>
                      <span className="inline-block w-4 h-4 loading-spinner border-2 mr-2 align-middle" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      Comecar!
                      <span className="ml-2">🚀</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skip Option */}
        <div className="text-center mt-4">
          <Button
            variant="link"
            onClick={onComplete}
            className="text-sm text-rsb-muted hover:text-rsb-text-secondary"
            aria-label="Pular configuracao inicial"
          >
            Pular por agora
          </Button>
        </div>
      </div>
    </div>
  )
}
